/**
 * Console transport — what a developer sees instead of a network request.
 *
 * On by default in development, and switchable on in a deployed build with
 * `VITE_LOGGING_CONSOLE=1` when someone needs to confirm what a real
 * browser is emitting without reading BigQuery.
 *
 * Everything prints at `console.debug`, including events whose severity is
 * ERROR. That looks wrong for about five seconds and then stops: the
 * Playwright suite fails a route on any `console.error`, so an error *event*
 * printed at error level would fail the build for every error the site
 * already handles deliberately. The underlying error still reaches
 * `console.error` from wherever it was actually caught — this transport is
 * echoing the telemetry record, not the failure.
 *
 * @module logging/transports/console-transport
 */

import type { Transport, WireEvent } from '../core/types';

/**
 * Prints immediately rather than batching. Waiting five seconds to show a
 * developer what just happened would defeat the point of the transport.
 */
export function createConsoleTransport(): Transport {
  return {
    name: 'console',

    accept(event: WireEvent): void {
      const { event_name: name, severity, event_seq: seq, ...rest } = event;
      console.debug(`[event] ${String(name)} #${String(seq)} ${String(severity)}`, rest);
    },

    flush(): void {
      // Nothing is held back, so there is nothing to flush.
    },
  };
}
